import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { NavLink, useNavigate } from 'react-router-dom';

// Components
import Modal from 'components/Modal';
import Steps from 'components/Steps';
import AlertError from 'components/AlertError';
import Primary from 'components/forms/segments/Primary';
import Segment1 from 'components/forms/segments/Segment1';
import Segment2 from 'components/forms/segments/Segment2';

// Data & Services
import quotes from 'data/quotes.json';
import { sendToGHL } from 'services/ghl';
import { getAccountID, getWebhookID } from 'services/wp';
import validateEmail from 'utils/validateEmail';
import { validatePhone } from 'utils/validatePhone';
import { validateZip } from 'utils/validateZip';
import setLocalStorage from 'utils/setLocalStorage';
import getDeviceFingerprint from 'utils/getDeviceFingerPrint';
import { withSubmissionTimeout, getSubmissionErrorMessage } from 'utils/submissionTimeout';

// Redux Actions
import { setValue } from 'routes/online_quotes/priceQuoteSlice';
import { setOnlineQuoteForm } from 'routes/online_quotes/content/onlineQuoteFormSlice';

const PopUpOnlineQuote = () => {
    // -- STATE MANAGEMENT --
    const [agreeToTerms, setAgreeToTerms] = useState(false);
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [showErrorMessage, setShowErrorMessage] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');
    const [open, setOpen] = useState(false);
    const [reset, setReset] = useState(false);
    const [sendingUnfinishedForm, setSendingUnfinishedForm] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [currentSegment, setCurrentSegment] = useState(0);
    const [attemptedSteps, setAttemptedSteps] = useState({ 0: false, 1: false, 2: false });
    const [submittedNextSteps, setSubmittedNextSteps] = useState({ 0: false, 1: false });

    // -- REDUX & ROUTER --
    const onlineQuoteForm = useSelector((state) => state.onlineQuoteForm.value);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    // -- HELPERS --
    const handleAgreeToTerms = (e) => {
        const { name, type, checked } = e.target;
        if (type === 'checkbox' && name === 'agreeToTerms') {
            setAgreeToTerms(checked);
        }
    };

    const cleanPhoneNumber = (phone) => phone ? phone.replace(/[()\-\s]/g, '') : '';

    const calculateQuotePrices = (formData) => {
        let quoteGoldPlan = null;
        let quoteSilverPlan = null;
        let age = parseInt(formData.age, 10);
        let spouseAge = parseInt(formData.spouseAge, 10);
        const pkg = formData.package;

        if (!pkg || isNaN(age) || age < 18 || age > 64 || !formData.tobaccoUser) {
            return { goldPlan: null, silverPlan: null };
        }

        // Average Age Logic
        if (['Couple', 'Family'].includes(pkg)) {
            if (isNaN(spouseAge) || spouseAge < 18 || spouseAge > 64) {
                return { goldPlan: null, silverPlan: null };
            }
            age = Math.round((age + spouseAge) / 2);
        }

        // Price Lookup Logic
        quotes.forEach(plan => {
            ['GOLD PLAN', 'SILVER PLAN'].forEach(planType => {
                if (plan[planType]) {
                    plan[planType].forEach(item => {
                        if (item.AGE === age) {
                            let price = null;
                            const isTobacco = formData.tobaccoUser === "Yes";
                            const dependents = parseInt(formData.numberOfDependents, 10) || 0;

                            if (pkg === 'Individual') {
                                price = isTobacco ? item['Single T'] : item['Single NT'];
                            } else if (pkg === 'Couple' || pkg === 'Individual + Child') {
                                price = isTobacco ? item['Couple T'] : item['Couple NT'];
                            } else if (pkg.includes('Family') || pkg.includes('Children')) {
                                const base = isTobacco ? item['Family T'] : item['Family NT'];
                                price = dependents > 3 ? base + ((dependents - 3) * 50) : base;
                            }

                            if (planType === 'GOLD PLAN') quoteGoldPlan = price;
                            if (planType === 'SILVER PLAN') quoteSilverPlan = price;
                        }
                    });
                }
            });
        });

        return { goldPlan: quoteGoldPlan, silverPlan: quoteSilverPlan };
    };

    const prepareJsonData = (formData, calculatedPrices = null) => {
        const deviceData = getDeviceFingerprint();
        const prices = calculatedPrices || calculateQuotePrices(formData);
        const goldPlan = prices?.goldPlan !== null && prices?.goldPlan !== undefined ? parseInt(prices.goldPlan, 10) : null;
        const silverPlan = prices?.silverPlan !== null && prices?.silverPlan !== undefined ? parseInt(prices.silverPlan, 10) : null;
        const hasValidQuotePrices = !isNaN(goldPlan) && goldPlan !== null && !isNaN(silverPlan) && silverPlan !== null;

        return {
            package: hasValidQuotePrices ? (formData.package || '') : '',
            firstName: formData.firstName || '',
            lastName: formData.lastName || '',
            email: formData.email || '',
            phone: cleanPhoneNumber(formData.phone),
            age: hasValidQuotePrices ? (parseInt(formData.age, 10) || null) : null,
            gender: formData.gender || '',
            state: formData.state || '',
            zipCode: formData.zipCode || '',
            tobaccoUser: hasValidQuotePrices ? (formData.tobaccoUser || '') : '',
            medications: hasValidQuotePrices ? (formData.medications || '') : '',
            spouseAge: hasValidQuotePrices ? (parseInt(formData.spouseAge, 10) || null) : null,
            numberOfDependents: hasValidQuotePrices ? (parseInt(formData.numberOfDependents, 10) || null) : null,
            dependent1Age: hasValidQuotePrices ? (parseInt(formData.dependent1Age, 10) || null) : null,
            dependent2Age: hasValidQuotePrices ? (parseInt(formData.dependent2Age, 10) || null) : null,
            dependent3Age: hasValidQuotePrices ? (parseInt(formData.dependent3Age, 10) || null) : null,
            dependent4Age: hasValidQuotePrices ? (parseInt(formData.dependent4Age, 10) || null) : null,
            dependent5Age: hasValidQuotePrices ? (parseInt(formData.dependent5Age, 10) || null) : null,
            dependent6Age: hasValidQuotePrices ? (parseInt(formData.dependent6Age, 10) || null) : null,
            dependent7Age: hasValidQuotePrices ? (parseInt(formData.dependent7Age, 10) || null) : null,
            quotePrice: {
                goldPlan: hasValidQuotePrices ? goldPlan : null,
                silverPlan: hasValidQuotePrices ? silverPlan : null
            },
            ...deviceData
        };
    };

    // -- CORE SUBMISSION LOGIC --
    const executeGHLSubmission = async (dataPayload) => {
        try {
            await withSubmissionTimeout(async () => {
                // 1. Fetch IDs (Now throws error if fails)
                const [accountID, webhookID] = await Promise.all([
                    getAccountID(),
                    getWebhookID({ formName: "online_quote" })
                ]);
                
                // 2. Send to GHL (Now throws error if fails)
                await sendToGHL(dataPayload, accountID, webhookID);
            });
            
            return true; // Success
        } catch (error) {
            console.error("GHL Submission Failed:", error);
            throw error; // Re-throw to be caught by caller
        }
    };

    // -- MODAL ACTIONS --
    const closeModal = () => {
        setOpen(false);
        document.body.style.overflow = "auto";
    };

    const openModal = () => {
        setReset(false);
        setAttemptedSteps({ 0: false, 1: false, 2: false });
        setSubmittedNextSteps({ 0: false, 1: false });
        setOpen(true);
        document.body.style.overflow = "hidden";
    };

    const handleOpenConfirmation = () => document.getElementById('confirmation_modal').showModal();
    const cancelConfirmation = () => document.getElementById('confirmation_modal').close();

    // -- CLOSING UNFINISHED FORM --
    const closeConfirmation = async () => {
        setSendingUnfinishedForm(true);
        
        if (validateEmail(onlineQuoteForm.email)) {
            const jsonData = prepareJsonData(onlineQuoteForm);
            // We suppress errors here because we don't want to alert the user if "abandonment tracking" fails
            try {
                await executeGHLSubmission(jsonData);
            } catch (e) {
                console.warn("Abandonment tracking failed silently:", e);
            }
        }

        setSendingUnfinishedForm(false);
        document.getElementById('confirmation_modal').close();
        closeModal();

        // Reset State
        setReset(true);
        setAttemptedSteps({ 0: false, 1: false, 2: false });
        setSubmittedNextSteps({ 0: false, 1: false });
        dispatch(setOnlineQuoteForm({ /* ... Reset all fields logic remains same ... */
            package: '', firstName: '', lastName: '', email: '', phone: '',
            age: null, gender: '', state: '', zipCode: '', tobaccoUser: '',
            medications: '', spouseAge: null, numberOfDependents: null,
            dependent1Age: null, dependent2Age: null, dependent3Age: null,
            dependent4Age: null, dependent5Age: null, dependent6Age: null, dependent7Age: null,
        }));
        setCurrentSegment(0);
    };

    // -- NAVIGATION & SUBMISSION --
    const handleBack = (e) => {
        if (e) e.preventDefault();
        if (currentSegment > 0) setCurrentSegment(currentSegment - 1);
    };

    const scrollFirstInvalid = () => {
        setTimeout(() => {
            const firstInvalid = document.querySelector('[data-invalid="true"]');
            if (firstInvalid) {
                firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 50);
    };

    const handleNextSubmit = async (e) => {
        e.preventDefault();
        setAttemptedSteps(prev => ({ ...prev, [currentSegment]: true }));

        // Validate Segment 0 (Primary info)
        if (currentSegment === 0) {
            const missing = [];
            if (!onlineQuoteForm.firstName?.trim()) missing.push('First Name');
            if (!onlineQuoteForm.lastName?.trim()) missing.push('Last Name');
            if (!onlineQuoteForm.email?.trim()) {
                missing.push('Email');
            } else if (!validateEmail(onlineQuoteForm.email)) {
                missing.push('Email (Invalid format)');
            }
            if (missing.length > 0) {
                scrollFirstInvalid();
                return;
            }
        } else if (currentSegment === 1) {
            const missing = [];
            if (!onlineQuoteForm.phone?.trim()) {
                missing.push('Phone');
            } else if (!validatePhone(onlineQuoteForm.phone)) {
                missing.push('Phone (10 digits)');
            }
            if (!onlineQuoteForm.state) missing.push('State');
            if (onlineQuoteForm.zipCode?.trim() && !validateZip(onlineQuoteForm.zipCode)) {
                missing.push('Zip Code (5 digits)');
            }
            if (!onlineQuoteForm.gender) missing.push('Gender');

            if (missing.length > 0) {
                scrollFirstInvalid();
                return;
            }
        }

        // 1. HANDLE "NEXT" BUTTON (Initial Submission)
        if (currentSegment < segments.length - 1) {
            if (submittedNextSteps[currentSegment]) {
                setCurrentSegment(currentSegment + 1);
                return;
            }

            setIsLoading(true);
            const jsonData = prepareJsonData(onlineQuoteForm);

            try {
                await executeGHLSubmission(jsonData);
                setSubmittedNextSteps(prev => ({ ...prev, [currentSegment]: true }));
                setCurrentSegment(currentSegment + 1);
            } catch (error) {
                console.error("Partial submission failed:", error);
                setErrorMessage(getSubmissionErrorMessage(error));
                setShowErrorMessage(true);
                setCurrentSegment(currentSegment + 1); // Allow user to continue locally even if API failed
            } finally {
                setIsLoading(false);
            }

        } 
        // 2. HANDLE "SUBMIT" BUTTON (Final Calculation & Submission)
        else {
            const missing = [];
            if (!onlineQuoteForm.package) missing.push('Package');
            const ageNum = parseInt(onlineQuoteForm.age, 10);
            if (!String(onlineQuoteForm.age || '').trim()) {
                missing.push('Your Age');
            } else if (isNaN(ageNum) || ageNum < 18 || ageNum > 64) {
                missing.push('Your Age (18–64)');
            }
            if (['Couple', 'Family'].includes(onlineQuoteForm.package)) {
                const spouseAgeNum = parseInt(onlineQuoteForm.spouseAge, 10);
                if (!String(onlineQuoteForm.spouseAge || '').trim()) {
                    missing.push('Spouse Age');
                } else if (isNaN(spouseAgeNum) || spouseAgeNum < 18 || spouseAgeNum > 64) {
                    missing.push('Spouse Age (18–64)');
                }
            }
            if (!onlineQuoteForm.tobaccoUser) missing.push('Tobacco User');
            if (onlineQuoteForm.package && !['Couple', 'Individual'].includes(onlineQuoteForm.package) && !onlineQuoteForm.numberOfDependents) {
                missing.push('Number of Dependents');
            }
            if (!agreeToTerms) missing.push('Terms & Conditions Agreement');

            if (missing.length > 0) {
                scrollFirstInvalid();
                return;
            }

            setIsLoading(true);
            setShowErrorMessage(false);

            // -- Calculation Logic --
            const { goldPlan: quoteGoldPlan, silverPlan: quoteSilverPlan } = calculateQuotePrices(onlineQuoteForm);

            if (quoteGoldPlan === null || quoteSilverPlan === null) {
                setIsLoading(false);
                setErrorMessage('Unable to calculate quote prices for the entered information. Please check your inputs and try again.');
                setShowErrorMessage(true);
                return;
            }

            // Store Prices in Redux
            dispatch(setValue({ goldPlan: quoteGoldPlan, silverPlan: quoteSilverPlan }));

            // Prepare Final Data
            const jsonData = prepareJsonData(onlineQuoteForm, { goldPlan: quoteGoldPlan, silverPlan: quoteSilverPlan });

            try {
                // SUBMIT TO GHL
                await executeGHLSubmission(jsonData);

                // LOCAL STORAGE BACKUP
                localStorage.removeItem('quoteForm');
                localStorage.removeItem('priceQuote');
                setLocalStorage('quoteForm', JSON.stringify(jsonData), 1000 * 60 * 60);

                // SUCCESS UI
                setShowSuccessMessage(true);
                setTimeout(() => setShowSuccessMessage(false), 2000);

                // RESET & NAVIGATE
                dispatch(setOnlineQuoteForm({
                    package: '', firstName: '', lastName: '', email: '', phone: '',
                    age: null, gender: '', state: '', zipCode: '', tobaccoUser: '',
                    medications: '', spouseAge: null, numberOfDependents: null,
                    dependent1Age: null, dependent2Age: null, dependent3Age: null,
                    dependent4Age: null, dependent5Age: null, dependent6Age: null, dependent7Age: null,
                }));
                setAgreeToTerms(false);
                setAttemptedSteps({ 0: false, 1: false, 2: false });
                setSubmittedNextSteps({ 0: false, 1: false });
                closeModal();
                navigate(onlineQuoteForm.state === "California" ? '/price-quote-california' : '/price-quote');

            } catch (error) {
                // FAILURE UI
                console.error("Final Submission Failed:", error);
                setErrorMessage(getSubmissionErrorMessage(error));
                setShowErrorMessage(true);
                setAgreeToTerms(false);
            } finally {
                setIsLoading(false);
            }
        }
    };

    const segments = [
        { component: <Primary reset={reset} showErrors={attemptedSteps[0]} />, title: "Step 1" },
        { component: <Segment1 showErrors={attemptedSteps[1]} />, title: "Step 2" },
        { component: <Segment2 agreeToTerms={agreeToTerms} handleAgreeToTerms={handleAgreeToTerms} showErrors={attemptedSteps[2]} />, title: "Show Quotes" },
    ];


    return (
        <>

            <div>
                {/* DIALOG CLOSING CONFIRMATION */}
                <dialog id="confirmation_modal" className="modal">
                    <div className="modal-box">
                        <div className="font-bold text-lg">Are you sure you want to close?</div>
                        <div className="py-4 text-md">If you close before getting your quote, all your information will be lost.</div>
                        <div className="modal-action flex justify-between">
                            <button className="btn btn-secondary text-base-100" disabled={sendingUnfinishedForm} onClick={cancelConfirmation}>
                                Cancel
                            </button>
                            <button className="btn btn-outline hover:btn-error hover:!text-base-100" disabled={sendingUnfinishedForm} onClick={closeConfirmation}>
                                {
                                    sendingUnfinishedForm ? "Closing..." : "Close"
                                }
                            </button>
                        </div>
                    </div>
                </dialog>
            </div>



            {/* MODAL QUOTE FORM */}
            <Modal open={open} handleClose={handleOpenConfirmation} closeButton={!isLoading} disableBackgroundClose={true}>
                <form onSubmit={handleNextSubmit} noValidate className='flex flex-col px-2 gap-y-4'>
                    <div className='text-center sm:mt-[-20px]'>
                        <div className='text-3xl font-bold'>Online Quote Form</div>
                        <div className='text-sm text-neutral'>Please fill up necessary informations</div>
                    </div>

                    {/* FORM SEGMENTS */}
                    <Steps segments={segments} numberOfChildrenListener={onlineQuoteForm.numberOfDependents} stepsCounter={currentSegment} />

                    {/* Next/Back Buttons */}
                    <div className='flex justify-between items-center'>
                        {
                            currentSegment > 0 ?
                                <button type="button" className='btn btn-primary' onClick={handleBack} disabled={isLoading}>Back</button> :
                                <div></div>
                        }

                        {/* T&C and Privacy Policy */}
                        <div className='flex justify-center underline text-blue-600 text-xs'>
                            <NavLink className='px-2' to='/terms-and-condition' target='_blank'>Terms and Conditions</NavLink>
                            <NavLink className='px-2' to='/privacy-policy' target='_blank'>Privacy Policy</NavLink>
                        </div>

                        <button type='submit' className='btn btn-primary' disabled={isLoading}>
                            {
                                currentSegment === segments.length - 1 ?

                                    isLoading ?
                                        <span className="loading loading-spinner loading-lg text-secondary"></span>
                                        :
                                        'Submit'

                                    : isLoading ?
                                        <span className="loading loading-spinner loading-lg text-secondary"></span>
                                        :
                                        'Next'
                            }
                        </button>

                    </div>

                </form>
            </Modal>

            <div className='flex flex-wrap justify-evenly items-center'>

                {/* TRIGGER FOR QUOTE FORM MODAL */}
                <div className='flex justify-center'>
                    <button className='btn btn-wide btn-lg border-white border-2 btn-secondary text-base-100' onClick={openModal}>GET YOUR QUOTE</button>
                </div>

                {/* ENROLL NOW BUTTON */}
                <div className='flex justify-center'>
                    <button className='btn btn-wide btn-lg border-white border-2 btn-secondary text-base-100' onClick={() => { localStorage.removeItem('quoteForm'); localStorage.removeItem('priceQuote'); navigate('/enrollment'); }}>ENROLL NOW</button>
                </div>
            </div>

            {showSuccessMessage && (
                <div className="toast toast-center toast-middle z-[9999]">
                    <div className="alert alert-success text-base-100">
                        <span>Message sent successfully.</span>
                    </div>
                </div>
            )}
            <AlertError showError={showErrorMessage} onClose={() => setShowErrorMessage(false)}>
                {errorMessage || 'Something went wrong. Please try again later.'}
            </AlertError>

        </>
    );
}

export default PopUpOnlineQuote;
